import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import PageBreadcrumbs from "@/components/PageBreadcrumbs";
import { Building2, ArrowRight, Search, MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cityData, topCities } from "@/utils/cityData";
import CTASection from "@/components/CTASection";
import StructuredData from "@/components/StructuredData";
import { getBreadcrumbSchema } from "@/utils/seoSchemas";
const CyberversicherungHub = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const allCitiesArray = Object.values(cityData);
  const filteredCities = allCitiesArray.filter(city => city.name.toLowerCase().includes(searchTerm.toLowerCase()));
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "InsuranceAgency",
    "name": "Praxis Versichert",
    "description": "Cyberversicherung für Arztpraxen in ganz Deutschland",
    "url": "https://praxis-versichert.de/cyberversicherung",
    "areaServed": allCitiesArray.map(city => ({
      "@type": "City",
      "name": city.name
    }))
  };

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: "Home", url: "https://praxis-versichert.de" },
    { name: "Cyberversicherung" }
  ]);
  return <>
      <Helmet>
        <title>Cyberversicherung Deutschland | Schutz für Arztpraxen in Ihrer Stadt</title>
        <meta name="description" content="Cyberversicherung für Arztpraxen in ganz Deutschland. Lokale Beratung in Berlin, München, Hamburg, Köln und 16 weiteren Städten. Jetzt kostenlos beraten lassen!" />
        <meta name="keywords" content="Cyberversicherung Deutschland, Arztpraxis Cyberschutz, regionale Cyberversicherung, lokale Versicherungsberatung" />
        <link rel="canonical" href="https://praxis-versichert.de/cyberversicherung" />
        <meta property="og:title" content="Cyberversicherung für Arztpraxen in Ihrer Stadt" />
        <meta property="og:description" content="Finden Sie die beste Cyberversicherung für Ihre Arztpraxis. Lokale Expertise in 20 deutschen Großstädten." />
        <meta property="og:url" content="https://praxis-versichert.de/cyberversicherung" />
        <meta property="og:type" content="website" />
      </Helmet>

      <StructuredData data={[organizationSchema, breadcrumbSchema]} />

      <div className="min-h-screen relative overflow-hidden">
        <div className="relative z-10">
          <Navigation />
          <PageBreadcrumbs items={[{
          label: "Home",
          path: "/"
        }, {
          label: "Cyberversicherung"
        }]} />

          {/* Hero Section */}
          <section className="py-16 sm:py-20 lg:py-24">
              <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="max-w-4xl mx-auto text-center">
                  <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">Cyberversicherung für
Arztpraxen in Deutschland</h1>
                  <p className="text-base sm:text-lg lg:text-xl text-muted-foreground mb-8 sm:mb-10">
                    Wählen Sie Ihre Stadt für lokale Experten-Beratung und maßgeschneiderten Cyberschutz
                  </p>

                  {/* Suchfunktion */}
                  <div className="max-w-xl mx-auto mb-12 sm:mb-16">
                    <div className="relative">
                      <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input type="search" placeholder="Stadt suchen (z.B. Berlin, München...)" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-12 pr-4 py-6 text-base sm:text-lg" />
                    </div>
                  </div>
                </div>
              </div>
            </section>


            {/* Alle Städte Grid */}
            <section className="py-12 sm:py-16 lg:py-20">
              <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="max-w-6xl mx-auto">
                  <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-center mb-8 sm:mb-10 lg:mb-12">
                    Alle Städte mit Cyberversicherung für Arztpraxen
                  </h2>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                    {filteredCities.map(city => <Link key={city.slug} to={`/cyberversicherung/${city.slug}`} className="group bg-card/50 backdrop-blur-sm border border-border/50 rounded-lg p-5 sm:p-6 hover:shadow-[var(--shadow-card)] hover:border-accent/50 transition-all">
                        <div className="flex items-start gap-4">
                          <Building2 className="w-10 h-10 sm:w-12 sm:h-12 text-accent flex-shrink-0 group-hover:scale-110 transition-transform" />
                          <div className="flex-1 min-w-0">
                            <h3 className="font-bold text-base sm:text-lg text-foreground mb-1 group-hover:text-accent transition-colors">
                              {city.name}
                            </h3>
                            <p className="text-xs sm:text-sm text-muted-foreground mb-2">
                              {city.state}
                            </p>
                            <div className="space-y-1">
                              <p className="text-xs text-muted-foreground">
                                📍 {city.practices} Arztpraxen
                              </p>
                              <p className="text-xs text-muted-foreground">
                                🏥 {city.hospitals} Krankenhäuser
                              </p>
                            </div>
                          </div>
                          <ArrowRight className="w-5 h-5 text-accent opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all flex-shrink-0" />
                        </div>
                      </Link>)}
                  </div>

                  {filteredCities.length === 0 && <div className="text-center py-12">
                      <p className="text-muted-foreground">
                        Keine Städte gefunden. Versuchen Sie eine andere Suche.
                      </p>
                    </div>}
                </div>
              </div>
            </section>

            {/* Warum regional? */}
            <section className="py-12 sm:py-16 lg:py-20">
              <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="max-w-4xl mx-auto">
                  <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-center mb-6 sm:mb-8">
                    Warum regionale Cyberversicherung?
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
                    <div className="text-center">
                      <div className="bg-accent/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                        <MapPin className="w-8 h-8 text-accent" />
                      </div>
                      <h3 className="font-bold text-base sm:text-lg mb-2">Lokale Expertise</h3>
                      <p className="text-sm text-muted-foreground">
                        Unsere Experten kennen die spezifischen Cyber-Bedrohungen in Ihrer Region
                      </p>
                    </div>

                    <div className="text-center">
                      <div className="bg-accent/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Building2 className="w-8 h-8 text-accent" />
                      </div>
                      <h3 className="font-bold text-base sm:text-lg mb-2">Schnelle Hilfe vor Ort</h3>
                      <p className="text-sm text-muted-foreground">
                        Im Schadensfall sind regionale IT-Forensiker innerhalb weniger Stunden bei Ihnen
                      </p>
                    </div>

                    <div className="text-center">
                      <div className="bg-accent/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                        <ArrowRight className="w-8 h-8 text-accent" />
                      </div>
                      <h3 className="font-bold text-base sm:text-lg mb-2">Maßgeschneiderte Tarife</h3>
                      <p className="text-sm text-muted-foreground">
                        Versicherungsschutz angepasst an lokale Anforderungen und Praxisstrukturen
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <CTASection title="Noch unsicher, welche Stadt?" subtitle="Kontaktieren Sie uns - wir beraten Sie gerne zu allen regionalen Angeboten und finden die beste Cyberversicherung für Ihre Praxis." />

          <Footer />
        </div>
      </div>
    </>;
};
export default CyberversicherungHub;